import { useState, useEffect } from "react";
import { CardListSkeleton } from "@/components/SkeletonLoaders";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/authContext";
import { useLocation } from "@/lib/locationContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { differenceInDays, parseISO, format } from "date-fns";
import SectionEmptyState from "@/components/SectionEmptyState";

interface PermitRenewal {
  id: string;
  permit_name: string;
  expiration_date: string;
  renewal_cost: number | null;
  notes: string | null;
}

function getDaysColor(days: number) {
  if (days < 0) return "text-destructive";
  if (days < 30) return "text-destructive";
  if (days <= 90) return "text-accent";
  return "text-primary";
}

function getDaysBg(days: number) {
  if (days < 0) return "bg-destructive/10 border-destructive/30";
  if (days < 30) return "bg-destructive/10 border-destructive/30";
  if (days <= 90) return "bg-accent/10 border-accent/30";
  return "bg-primary/10 border-primary/30";
}

export default function PermitRenewalTracker() {
  const { user } = useAuth();
  const { activeLocation } = useLocation();
  const locId = activeLocation?.id || null;
  const [permits, setPermits] = useState<PermitRenewal[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDate, setNewDate] = useState("");
  const [newCost, setNewCost] = useState("");

  const fetchPermits = async () => {
    if (!user) return;
    let q = supabase
      .from("permit_renewals")
      .select("*")
      .eq("user_id", user.id);
    if (locId) q = q.eq("location_id", locId);
    const { data } = await q.order("expiration_date", { ascending: true });
    setPermits((data as PermitRenewal[]) || []);
    setLoading(false);
  };

  useEffect(() => { fetchPermits(); }, [user, locId]);

  const addPermit = async () => {
    if (!user || !newName || !newDate) return;
    await supabase.from("permit_renewals").insert({
      user_id: user.id,
      permit_name: newName,
      expiration_date: newDate,
      renewal_cost: newCost ? parseInt(newCost) : null,
      location_id: locId,
    });
    setNewName(""); setNewDate(""); setNewCost(""); setShowAdd(false);
    toast.success("Permit added");
    fetchPermits();
  };

  const deletePermit = async (id: string) => {
    await supabase.from("permit_renewals").delete().eq("id", id);
    toast.success("Permit removed");
    fetchPermits();
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2"><Shield className="w-5 h-5 text-primary" /> Permit Renewal Tracker</CardTitle>
        <Button size="sm" variant="outline" onClick={() => setShowAdd(!showAdd)}><Plus className="w-4 h-4 mr-1" /> Add Permit</Button>
      </CardHeader>
      <CardContent>
        {showAdd && (
          <div className="mb-4 p-4 rounded-lg border border-border bg-muted/30 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label>Permit Name</Label><Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Business Tax Receipt" /></div>
              <div><Label>Expiration Date</Label><Input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} /></div>
              <div><Label>Renewal Cost ($)</Label><Input type="number" value={newCost} onChange={e => setNewCost(e.target.value)} placeholder="Optional" /></div>
            </div>
            <Button size="sm" onClick={addPermit} disabled={!newName || !newDate}>Save Permit</Button>
          </div>
        )}

        {loading ? (
          <CardListSkeleton rows={3} />
        ) : permits.length === 0 && !showAdd ? (
          <SectionEmptyState
            icon={Shield}
            title="No permits tracked yet"
            description="Track your business licenses and permits here. We'll alert you before renewal deadlines so you never lapse."
            actionLabel="Add a Permit"
            onAction={() => setShowAdd(true)}
          />
        ) : (
          <div className="space-y-3">
            {permits.map(p => {
              const days = differenceInDays(parseISO(p.expiration_date), new Date());
              return (
                <div key={p.id} className={`flex items-center justify-between p-3 rounded-lg border ${getDaysBg(days)}`}>
                  <div className="flex-1">
                    <div className="font-medium text-sm text-foreground">{p.permit_name}</div>
                    <div className="text-xs text-muted-foreground">
                      Expires: {format(parseISO(p.expiration_date), "MMM d, yyyy")}
                      {p.renewal_cost && ` · Renewal: $${p.renewal_cost.toLocaleString()}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`text-right font-medium text-lg ${getDaysColor(days)}`}>
                      {days < 0 ? `${Math.abs(days)}d overdue` : `${days}d`}
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => deletePermit(p.id)} className="h-8 w-8 text-muted-foreground hover:text-destructive">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
